import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { fetchPublicationsData } from '../../services/publicationsService';
import { getDailyRandomSeed } from '../../utils/dailyRandom';
import PDFViewer from '../PDFViewer';

/**
 * PublicationInsight Component
 * Displays a single publication card with daily rotation
 * Shows: Title, Speaker, Date - clean and simple
 */
const PublicationInsight = ({ googleSheetId, theme, isMobile }) => {
  const [publication, setPublication] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showPDFViewer, setShowPDFViewer] = useState(false);
  const [selectedPublication, setSelectedPublication] = useState(null);
  
  useEffect(() => {
    const loadPublication = async () => {
      try {
        // Check cache first (valid for 24 hours)
        const cacheKey = 'publication_insight_cache';
        const cached = localStorage.getItem(cacheKey);
        const today = new Date().toDateString();
        
        if (cached) {
          const parsedCache = JSON.parse(cached);
          if (parsedCache.date === today && parsedCache.publication) {
            setPublication(parsedCache.publication);
            setLoading(false);
            return;
          }
        }
        
        // Fetch publications
        const publications = await fetchPublicationsData(googleSheetId);
        
        if (!publications || publications.length === 0) {
          setLoading(false);
          return;
        }
        
        // Filter publications with valid URLs and titles
        const validPublications = publications.filter(p => p.pdfUrl && p.title);
        
        if (validPublications.length === 0) {
          setLoading(false);
          return;
        }
        
        // Use daily random to select a publication
        const seed = getDailyRandomSeed();
        const randomIndex = Math.abs(seed) % validPublications.length;
        const selectedPublication = validPublications[randomIndex];
        
        // Simple publication data
        const publicationData = {
          title: selectedPublication.title,
          speaker: selectedPublication.author || 'Unknown Speaker',
          date: selectedPublication.date || selectedPublication.year || '',
          url: selectedPublication.pdfUrl,
          type: selectedPublication.type || 'Publication'
        };
        
        // Cache for 24 hours
        localStorage.setItem(cacheKey, JSON.stringify({
          date: today,
          publication: publicationData
        }));
        
        setPublication(publicationData);
        
      } catch (error) {
        console.error('Error loading publication:', error);
      } finally {
        setLoading(false);
      }
    };
    
    loadPublication();
  }, [googleSheetId]);
  
  // Don't render if no publication or still loading
  if (loading || !publication) {
    return null;
  }
  
  // Format the date nicely
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    // If it's just a year, return as is
    if (/^\d{4}$/.test(dateStr)) return dateStr;
    // Otherwise try to format it nicely
    return dateStr;
  };
  
  return (
    <div style={{
      width: '100%',
      padding: isMobile ? '0 0 32px' : '0 20px 40px',
    }}>
      {/* Section Header - matching Videos/Shorts style */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '20px',
        paddingLeft: isMobile ? '0' : '4px',
        paddingRight: isMobile ? '0' : '4px'
      }}>
        <div>
          <h2 style={{
            fontSize: isMobile ? '20px' : '24px',
            fontWeight: '600',
            margin: 0,
            color: theme?.colors?.primary || '#0f172a'
          }}>
            Featured Publication
          </h2>
        </div>
        
        <Link 
          to="/publications" 
          style={{
            color: theme?.colors?.primary || '#0f172a',
            textDecoration: 'none',
            fontWeight: '600',
            fontSize: '16px',
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            transition: 'all 0.2s ease',
            opacity: 0.9
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.opacity = '1';
            e.currentTarget.style.transform = 'translateX(2px)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.opacity = '0.9';
            e.currentTarget.style.transform = 'translateX(0)';
          }}
        >
          View all
          <span style={{ fontSize: '18px' }}>→</span>
        </Link>
      </div>
      
      {/* Publication Card - Full width on mobile, centered with max-width on desktop */}
      <div style={{
        width: '100%',
        maxWidth: isMobile ? '100%' : '600px',
        margin: isMobile ? '0' : '0 auto',
        padding: '0'
      }}>
        <div
          onClick={() => {
            setSelectedPublication({
              pdfUrl: publication.url,
              title: publication.title,
              author: publication.speaker
            });
            setShowPDFViewer(true);
          }}
          style={{
            textDecoration: 'none',
            display: 'block',
            width: '100%',
            cursor: 'pointer'
          }}
        >
          <div
            style={{
              background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(249, 250, 251, 0.95) 100%)',
              borderRadius: isMobile ? '12px' : '16px',
              boxShadow: isMobile ? '0 4px 20px rgba(0, 0, 0, 0.06)' : '0 10px 40px rgba(0, 0, 0, 0.08)',
              padding: isMobile ? '24px 16px' : '32px 24px',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(255, 255, 255, 0.6)',
            transition: 'all 0.3s',
            cursor: 'pointer',
            position: 'relative',
            overflow: 'hidden',
            textAlign: 'center'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-2px)';
            e.currentTarget.style.boxShadow = isMobile ? '0 6px 24px rgba(0, 0, 0, 0.08)' : '0 12px 48px rgba(0, 0, 0, 0.12)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = isMobile ? '0 4px 20px rgba(0, 0, 0, 0.06)' : '0 10px 40px rgba(0, 0, 0, 0.08)';
          }}
        >
          {/* Gradient overlay decoration - matching Shorts style */}
          <div style={{
            position: 'absolute',
            top: '-50%',
            right: '-10%',
            width: '300px',
            height: '300px',
            background: `radial-gradient(circle, ${theme?.colors?.primary || '#0f172a'}15 0%, transparent 70%)`,
            borderRadius: '50%',
            pointerEvents: 'none',
            zIndex: 0
          }} />
            
            {/* Content */}
            <div style={{
              position: 'relative'
            }}>
              {/* Title */}
              <h3 style={{
                fontSize: isMobile ? '19px' : '22px',
                lineHeight: '1.4',
                color: theme?.colors?.cardText || '#1f2937',
                margin: '0 0 20px 0',
                fontWeight: '600'
              }}>
                {publication.title}
              </h3>
              
              {/* Speaker and Date */}
              <div style={{
                fontSize: '15px',
                color: theme?.colors?.cardTextSecondary || '#6b7280',
                marginBottom: '24px'
              }}>
                <div style={{ marginBottom: '6px' }}>
                  <span style={{ fontWeight: '500' }}>By:</span> {publication.speaker}
                </div>
                {publication.date && (
                  <div>
                    <span style={{ fontWeight: '500' }}>Published:</span> {formatDate(publication.date)}
                  </div>
                )}
              </div>
              
              {/* Call to action */}
              <div style={{
                fontSize: '15px',
                color: theme?.colors?.primary || '#0f172a',
                fontWeight: '600',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '4px'
              }}>
                Read Publication
                <span style={{ fontSize: '18px' }}>→</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* PDF Viewer Modal */}
      {showPDFViewer && selectedPublication && (
        <PDFViewer
          pdfUrl={selectedPublication.pdfUrl}
          title={selectedPublication.title}
          author={selectedPublication.author}
          onClose={() => {
            setShowPDFViewer(false);
            setSelectedPublication(null);
          }}
        />
      )}
    </div>
  );
};

export default PublicationInsight;